/**
 * Lightweight markdown renderer — tables, lists, code, bold, italic, headings.
 *
 * Optimised for Claude's chat output. Avoids pulling in `react-markdown` +
 * `remark-gfm` (~80 KB) since we render at most a few hundred lines.
 */

import React from "react";

const INLINE_RX = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/g;

function renderInline(text, keyPrefix = "") {
  if (!text) return null;
  const parts = text.split(INLINE_RX).filter((p) => p !== "");
  return parts.map((part, i) => {
    const k = `${keyPrefix}-i-${i}`;
    if (part.startsWith("**") && part.endsWith("**") && part.length > 4) {
      return (
        <strong key={k} className="font-semibold text-foreground">
          {part.slice(2, -2)}
        </strong>
      );
    }
    if (part.startsWith("*") && part.endsWith("*") && part.length > 2) {
      return (
        <em key={k} className="italic">
          {part.slice(1, -1)}
        </em>
      );
    }
    if (part.startsWith("`") && part.endsWith("`") && part.length > 2) {
      return (
        <code
          key={k}
          className="px-1.5 py-0.5 rounded-sm bg-secondary text-[0.85em] font-mono text-[#F58216]"
        >
          {part.slice(1, -1)}
        </code>
      );
    }
    const link = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(part);
    if (link) {
      return (
        <a
          key={k}
          href={link[2]}
          target="_blank"
          rel="noreferrer"
          className="text-[#F58216] underline-offset-2 hover:underline"
        >
          {link[1]}
        </a>
      );
    }
    return <span key={k}>{part}</span>;
  });
}

function parseTable(lines) {
  // Expect: header | header / | --- | --- | / | row | row |
  if (lines.length < 2) return null;
  const splitRow = (l) =>
    l
      .replace(/^\|/, "")
      .replace(/\|$/, "")
      .split("|")
      .map((c) => c.trim());

  const header = splitRow(lines[0]);
  const sep = lines[1];
  if (!/^\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)+\|?\s*$/.test(sep)) return null;
  const body = lines.slice(2).map(splitRow);
  return { header, body };
}

function Block({ block, blockKey }) {
  if (block.kind === "heading") {
    const Tag = `h${block.level}`;
    const sizes = {
      1: "text-xl font-display font-bold mt-4 mb-2",
      2: "text-lg font-display font-bold mt-4 mb-2",
      3: "text-base font-display font-semibold mt-3 mb-1.5",
      4: "text-sm font-semibold uppercase tracking-wider mt-3 mb-1 text-muted-foreground",
    };
    return (
      <Tag className={sizes[block.level] || sizes[3]}>
        {renderInline(block.text, blockKey)}
      </Tag>
    );
  }
  if (block.kind === "table") {
    return (
      <div className="my-2 overflow-x-auto rounded-sm border border-border">
        <table className="w-full text-xs">
          <thead className="bg-secondary text-muted-foreground">
            <tr>
              {block.header.map((h, i) => (
                <th
                  key={`${blockKey}-th-${i}`}
                  className="px-2.5 py-1.5 text-left font-semibold uppercase tracking-wider"
                >
                  {renderInline(h, `${blockKey}-th-${i}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {block.body.map((row, ri) => (
              <tr key={`${blockKey}-tr-${ri}`} className="border-t border-border">
                {row.map((cell, ci) => (
                  <td key={`${blockKey}-td-${ri}-${ci}`} className="px-2.5 py-1.5 align-top">
                    {renderInline(cell, `${blockKey}-td-${ri}-${ci}`)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  if (block.kind === "ul") {
    return (
      <ul className="my-2 ml-5 list-disc space-y-1">
        {block.items.map((it, i) => (
          <li key={`${blockKey}-li-${i}`}>{renderInline(it, `${blockKey}-li-${i}`)}</li>
        ))}
      </ul>
    );
  }
  if (block.kind === "ol") {
    return (
      <ol className="my-2 ml-5 list-decimal space-y-1">
        {block.items.map((it, i) => (
          <li key={`${blockKey}-oi-${i}`}>{renderInline(it, `${blockKey}-oi-${i}`)}</li>
        ))}
      </ol>
    );
  }
  if (block.kind === "code") {
    return (
      <pre className="my-2 p-3 rounded-sm bg-secondary text-[11px] font-mono overflow-x-auto whitespace-pre">
        <code>{block.text}</code>
      </pre>
    );
  }
  // paragraph
  return (
    <p className="my-1.5 leading-relaxed whitespace-pre-wrap break-words">
      {renderInline(block.text, blockKey)}
    </p>
  );
}

export default function MarkdownLite({ text, className = "" }) {
  if (!text) return null;
  const lines = text.replace(/\r/g, "").split("\n");
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (line === "") {
      i += 1;
      continue;
    }
    // Code fence
    if (line.startsWith("```")) {
      const buf = [];
      i += 1;
      while (i < lines.length && !lines[i].startsWith("```")) {
        buf.push(lines[i]);
        i += 1;
      }
      i += 1; // skip closing fence
      blocks.push({ kind: "code", text: buf.join("\n") });
      continue;
    }
    // Heading
    const h = /^(#{1,4})\s+(.+)/.exec(line);
    if (h) {
      blocks.push({ kind: "heading", level: h[1].length, text: h[2] });
      i += 1;
      continue;
    }
    // Table
    if (line.startsWith("|") && i + 1 < lines.length && /^\|?\s*:?-+:?/.test(lines[i + 1])) {
      const tbl = [line];
      let j = i + 1;
      while (j < lines.length && lines[j].trim().startsWith("|")) {
        tbl.push(lines[j]);
        j += 1;
      }
      const parsed = parseTable(tbl);
      if (parsed) {
        blocks.push({ kind: "table", ...parsed });
        i = j;
        continue;
      }
    }
    // Unordered list
    if (/^\s*[-*]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*]\s+/, ""));
        i += 1;
      }
      blocks.push({ kind: "ul", items });
      continue;
    }
    // Ordered list
    if (/^\s*\d+\.\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*\d+\.\s+/, ""));
        i += 1;
      }
      blocks.push({ kind: "ol", items });
      continue;
    }
    // Paragraph (greedy until blank line / known block start)
    const buf = [line];
    let j = i + 1;
    while (
      j < lines.length &&
      lines[j] !== "" &&
      !lines[j].startsWith("```") &&
      !/^(#{1,4})\s+/.test(lines[j]) &&
      !lines[j].startsWith("|") &&
      !/^\s*[-*]\s+/.test(lines[j]) &&
      !/^\s*\d+\.\s+/.test(lines[j])
    ) {
      buf.push(lines[j]);
      j += 1;
    }
    blocks.push({ kind: "para", text: buf.join("\n") });
    i = j;
  }

  return (
    <div className={`text-sm text-foreground ${className}`}>
      {blocks.map((b, i) => (
        <Block key={`b-${i}`} block={b} blockKey={`b-${i}`} />
      ))}
    </div>
  );
}
